// SU knowledge tree loader — v0M9.2.
//
// Loads the researchable knowledge from src/data/generated/knowledge.json
// (produced by scripts/convert-xlsx.ts against docs/KnowledgeTree.xlsx —
// KnowledgeUnlocks sheet). Each entry carries a single-parent prereq
// (empty string = tier-1 root), a Learning cost, and a free-text Effects
// string that runtime consumers interpret per-effect.

import { assertSchemaVersion, KNOWLEDGE_SCHEMA_VERSION } from './loader';
import rawBlob from './generated/knowledge.json';

export interface KnowledgeSpec {
  /** Category — Strategic Warfare, Tactical Warfare, Logistics, ... */
  readonly category: string;
  /** Tier within its chain. 1 = root. */
  readonly level: number;
  /**
   * Parent knowledge name — empty string for tier-1 roots. Chains are
   * strictly linear at v1 (single parent), unlike Structures which
   * support comma-separated multi-prereq.
   */
  readonly prerequisite: string;
  /**
   * Free-text designer effects, e.g. "Increase Max Army + 1" or
   * "Unlocks Structure: Engineering Guild". Informational at v1 —
   * per-effect runtime consumers land as each effect is wired.
   */
  readonly effects: string;
  /** Research time in game-days (uniform 40 at v1). */
  readonly researchDays: number;
  /** Learning cost deducted from Faction.resources.learning on start_research. */
  readonly learningCost: number;
  /** Free-text description surfaced in the Knowledge modal. */
  readonly description: string;
}

export type Knowledge = Readonly<Record<string, KnowledgeSpec>>;

/** Load the full knowledge tree. Fails loudly on schemaVersion mismatch. */
export function loadKnowledge(): Knowledge {
  assertSchemaVersion(rawBlob, KNOWLEDGE_SCHEMA_VERSION, 'src/data/generated/knowledge.json');
  return (rawBlob as unknown as { knowledge: Knowledge }).knowledge;
}

/** Retrieve a knowledge by name; throws on unknown names. */
export function getKnowledge(knowledge: Knowledge, name: string): KnowledgeSpec {
  const entry = knowledge[name];
  if (!entry) {
    throw new Error(`Unknown knowledge: ${name}. Known: ${Object.keys(knowledge).slice(0, 5).join(', ')}...`);
  }
  return entry;
}

/**
 * Convenience: group the knowledge dict by category, preserving the
 * xlsx row order within each category (Object.keys() on a JSON blob
 * matches insertion order). Used by the Knowledge modal to render one
 * column per category.
 */
export function groupKnowledgeByCategory(knowledge: Knowledge): Record<string, KnowledgeSpec[]> {
  const groups: Record<string, KnowledgeSpec[]> = {};
  for (const name of Object.keys(knowledge)) {
    const spec = knowledge[name]!;
    if (!groups[spec.category]) groups[spec.category] = [];
    groups[spec.category]!.push(spec);
  }
  return groups;
}
